import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from '@material-ui/core'
import HealthCenterReportStyle from 'pages/HealthCenter/ReportV1/Style'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import Analysis from 'pages/HealthCenter/ReportV1/Elements/Analysis'
import {getPublicFile} from 'helpers/envHelper'
import PercentBarCardV3 from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/PercentBarCardV3'
import {useAppSelector} from 'hooks'
import {
  getCurrentLanguageReport,
  selectLanguageReport,
} from 'features/languageReport/languageReportSlice'

const useStyles = makeStyles(HealthCenterReportStyle)

export interface BrainFunctionalConnectivityProps {
  partTwoImg: string
  partThreeImg: string
  brainConnectivityBetweenLobesScore: {
    vertical: {
      left: number
      right: number
    }
    horizontal: number
  } | null
  brainConnectivityByLobeScore: {
    frontal: {
      left: number
      right: number
    }
    pariocci: {
      left: number
      right: number
    }
  } | null
}

const PagePrintWrap2 = ({
  partTwoImg,
  partThreeImg,
  brainConnectivityByLobeScore,
  brainConnectivityBetweenLobesScore,
}: BrainFunctionalConnectivityProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const getHealthReportReportImage = getPublicFile(
    'images/health_center_report',
  )
  const languageReport = useAppSelector((state) =>
    selectLanguageReport(state.languageReport),
  )
  const currentLanguagueReport = getCurrentLanguageReport(languageReport, 'es')

  const textBrainConnectivity: any = {
    ...currentLanguagueReport?.health?.brain_connectivity,
  }

  const interData = {...textBrainConnectivity.section.inter_connectivity}

  const getNumber = (value: number | string) => {
    if (!Number.isNaN(Number(value))) return Math.floor(Number(value))
    return 0
  }

  const getText = (value = 0, key: string) => {
    if (value < 30) {
      return t(`IBot${key}`, {
        value: getNumber(value),
      })
    }
    if (value > 70) {
      return t(`ITop${key}`, {
        value: getNumber(value),
      })
    }
    return t(`IMid${key}`, {
      value: getNumber(value),
    })
  }

  const getTextV2 = (left = 0, right = 0, key = 'Title') => {
    if (left < 30 && right < 30) {
      return `IAnalysisResultsDes${key}Case3`
    }
    if (left > 70 && right > 70) {
      return `IAnalysisResultsDes${key}Case2`
    }
    if (left > 70 && right < 70) {
      return `IAnalysisResultsDes${key}Case4`
    }
    if (left < 70 && right > 70) {
      return `IAnalysisResultsDes${key}Case5`
    }
    return `IAnalysisResultsDes${key}Case1`
  }

  const textPart2Text1 = useMemo(() => {
    const key = getTextV2(
      getNumber(brainConnectivityBetweenLobesScore?.vertical?.left ?? 0),
      getNumber(brainConnectivityBetweenLobesScore?.vertical?.right ?? 0),
      'Text',
    )
    return t(key, {
      left: getNumber(brainConnectivityBetweenLobesScore?.vertical?.left ?? 0),
      right: getNumber(
        brainConnectivityBetweenLobesScore?.vertical?.right ?? 0,
      ),
    })
  }, [brainConnectivityBetweenLobesScore?.vertical])

  const textPart2Title2 = useMemo(() => {
    return getText(
      brainConnectivityBetweenLobesScore?.horizontal ?? 0,
      'LeftRightBrainAnalysisResultsTitle',
    )
  }, [brainConnectivityBetweenLobesScore?.horizontal])

  const textPart2Text2 = useMemo(() => {
    return getText(
      brainConnectivityBetweenLobesScore?.horizontal ?? 0,
      'LeftRightBrainAnalysisResultsText',
    )
  }, [brainConnectivityBetweenLobesScore?.horizontal])
  const replaceMultiText = (text: string, values: (string | number)[]) => {
    return text.replace(/{(\d+)}/g, (_, index) => values[+index].toString())
  }
  const textPart2Title1V1 = useMemo(() => {
    const data = interData?.analysis_results?.opinions?.vertical
    const scoreLeft = getNumber(
      brainConnectivityBetweenLobesScore?.vertical?.left ?? 0,
    )
    const scoreRight = getNumber(
      brainConnectivityBetweenLobesScore?.vertical?.right ?? 0,
    )
    const average = Math.abs(scoreLeft - scoreRight)
    if (average <= 10) {
      if (scoreLeft > 70 || scoreRight > 70) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }
      if (scoreLeft < 30 || scoreRight < 30) {
        return replaceMultiText(data.difference_under.low.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_under.normal.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    if (scoreLeft > scoreRight) {
      if (scoreLeft > 70 && scoreRight > 70) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_over.higher_left.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    if (scoreLeft < scoreRight) {
      if (scoreLeft > 70 && scoreRight > 70) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_over.higher_right.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    return ''
  }, [brainConnectivityBetweenLobesScore?.vertical])
  return (
    <div className={classes.brainBasic}>
      <WrapElement number={2} text={interData.title}>
        <div>
          <div className={classes.brainConnectivityBrainRegionTabs2}>
            <div
              className={`${classes.cardLeftContent} ${classes.cardLeftContentAdditional}`}
            >
              <img
                className={classes.cardLeftImg}
                src={getHealthReportReportImage('brainRegions.webp')}
                alt='Contour'
                style={{width: '285px'}}
              />
            </div>
            <div className={classes.percentBarCardV3}>
              <PercentBarCardV3
                index={1}
                headerName={interData.gauge_bars.vertical.left.title}
                percentage={getNumber(
                  brainConnectivityBetweenLobesScore?.vertical?.left ?? 0,
                )}
                top={interData.gauge_bars.vertical.left.status.high}
                middle={interData.gauge_bars.vertical.left.status.normal}
                bottom={interData.gauge_bars.vertical.left.status.low}
              />
              <PercentBarCardV3
                index={2}
                headerName={interData.gauge_bars.vertical.right.title}
                percentage={getNumber(
                  brainConnectivityBetweenLobesScore?.vertical?.right ?? 0,
                )}
                top={interData.gauge_bars.vertical.right.status.high}
                middle={interData.gauge_bars.vertical.right.status.normal}
                bottom={interData.gauge_bars.vertical.right.status.low}
              />
              <PercentBarCardV3
                index={3}
                headerName={interData.gauge_bars.horizontal.title}
                percentage={getNumber(
                  brainConnectivityBetweenLobesScore?.horizontal ?? 0,
                )}
                top={interData.gauge_bars.horizontal.status.high}
                middle={interData.gauge_bars.horizontal.status.normal}
                bottom={interData.gauge_bars.horizontal.status.low}
              />
            </div>
          </div>
          <div
            className={`${classes.cardRightContent} ${classes.cardRightContentAdditional}`}
          >
            <div>
              <p className={classes.textBold}>{interData.description.main}</p>
              <br />
              <p className={classes.textBlack}>{interData.description.sub}</p>
            </div>
            <Analysis title={interData.analysis_results.title}>
              <div className={classes.connectivityBrain}>
                <p className={classes.textBold}>{textPart2Title1V1}</p>
                <p className={classes.textP} style={{marginBottom: 16}}>
                  {textPart2Text1}
                </p>

                <p className={classes.textBold}>{textPart2Title2}</p>
                <p className={classes.textP}>{textPart2Text2}</p>
              </div>
            </Analysis>
          </div>
        </div>
      </WrapElement>
    </div>
  )
}

export default PagePrintWrap2
